// Phone back button / swipe-back closes an open overlay (search, menu, sheet,
// modal, camera) instead of leaving the page underneath.
//
// While the overlay is open it owns one extra history entry (same URL, a
// marker in history.state):
//   - back → the browser pops that entry → we close the overlay;
//   - closed from the UI (X, backdrop) → we pop our own entry, so the next
//     back really goes back;
//   - a link inside the overlay navigates → that navigation REPLACES our
//     entry (router guard), so there is no dead "page + overlay" step left
//     in history and one back returns to the page the visitor came from.
import { watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

let seq = 0
// A page reload restarts seq, and history.state survives it: without a
// per-load prefix a stale marker could match a new overlay's token.
const LOAD = Math.random().toString(36).slice(2, 8)

export function useBackToClose(isOpen: () => boolean, close: () => void) {
  const router = useRouter()
  const token = `overlay-${LOAD}-${++seq}`
  let pushed = false
  let navigated = false

  function onPop() {
    if (!pushed || (window.history.state as any)?.__overlay === token) return
    pushed = false
    window.removeEventListener('popstate', onPop)
    if (isOpen()) close()
  }

  const removeGuard = router.beforeEach((to, from) => {
    if (!pushed || to.fullPath === from.fullPath) return true
    pushed = false
    navigated = true
    window.removeEventListener('popstate', onPop)
    return { path: to.path, query: to.query, hash: to.hash, replace: true }
  })

  watch(isOpen, (open) => {
    if (typeof window === 'undefined') return
    if (open) {
      if (pushed) return
      window.history.pushState({ ...(window.history.state || {}), __overlay: token }, '')
      pushed = true
      navigated = false
      window.addEventListener('popstate', onPop)
      return
    }
    if (!pushed) return
    // Wait a beat: a close that is immediately followed by a navigation
    // (search result, menu link) is handled by the guard above instead.
    setTimeout(() => {
      // Reopened within the beat: the entry is still needed.
      if (!pushed || navigated || isOpen()) return
      pushed = false
      window.removeEventListener('popstate', onPop)
      if ((window.history.state as any)?.__overlay === token) window.history.back()
    }, 80)
  })

  onBeforeUnmount(() => {
    removeGuard()
    window.removeEventListener('popstate', onPop)
    if (pushed && (window.history.state as any)?.__overlay === token) window.history.back()
  })
}
